import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { RelatedService } from 'src/pages/billing/billing.types';
import { useCreateRelatedServiceMutation } from 'src/pages/billing/billingApi';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { ServiceIndependentFormData, serviceIndependentFormSchema } from '../RelatedService.schema';

type ServiceIndependentFormHookProps = {
  setEditMode: (editMode: boolean) => void;
  relatedService: RelatedService;
};

const defaultValues: ServiceIndependentFormData = {
  serviceType: 'SP',
  startDate: '',
  providerName: '',
  providerPhoneNumber: '',
};

export const useServiceIndependentForm = ({
  setEditMode,
  relatedService,
}: ServiceIndependentFormHookProps) => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [createRelatedService, { isLoading }] = useCreateRelatedServiceMutation();

  const initialValues: ServiceIndependentFormData = {
    serviceType: relatedService.serviceType || defaultValues.serviceType,
    startDate: relatedService.startDate || defaultValues.startDate,
    providerName: relatedService.providerName || defaultValues.providerName,
    providerPhoneNumber: relatedService.providerPhoneNumber || defaultValues.providerPhoneNumber,
  };

  const methods = useForm<ServiceIndependentFormData>({
    resolver: zodResolver(serviceIndependentFormSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    try {
      await createRelatedService({
        id: id!,
        payload: {
          serviceType: data.serviceType,
          status: 'ServicedIndependently',
          startDate: data.startDate,
          providerName: data.providerName,
          providerPhoneNumber: data.providerPhoneNumber,
        },
      }).unwrap();

      dispatch(addToast({ text: 'Form saved successfully', type: 'positive' }));
      setEditMode(false);
    } catch (error) {
      dispatch(addToast({ text: 'Error saving form', type: 'negative' }));
      console.error('Error saving form:', error);
    }
  });

  return {
    methods,
    handleSubmit,
    isLoading,
  };
};
